import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Check, X, Download, Clock, ChevronDown, ChevronUp, MessageSquare, HelpCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

function formatCHF(val) {
  if (!val && val !== 0) return "—";
  return `CHF ${Number(val).toLocaleString("de-CH", { minimumFractionDigits: 2 })}`;
}

const STATUS_MAP = {
  pending: { label: "Ausstehend", color: "#D4A373", bg: "#FFF8F0", icon: Clock },
  accepted: { label: "Akzeptiert", color: "#10B981", bg: "#ECFDF5", icon: Check },
  rejected: { label: "Abgelehnt", color: "#EF4444", bg: "#FEF2F2", icon: X },
};

export default function PortalOffers({ projectId, offers, invoices = [], onUpdate }) {
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(null);
  const [questionText, setQuestionText] = useState("");
  const [loading, setLoading] = useState(false);

  const respond = async (offerNumber, action, comment = "") => {
    setLoading(true);
    try {
      await axios.post(`${API}/portal/${projectId}/offers/${offerNumber}/respond`, { action, comment });
      toast.success(action === "accepted" ? "Angebot akzeptiert" : "Angebot abgelehnt");
      if (onUpdate) onUpdate();
      setShowRejectModal(null);
      setRejectComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Antwort");
    } finally {
      setLoading(false);
    }
  };

  const askQuestion = async (offerNumber) => {
    if (!questionText.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/portal/${projectId}/offers/${offerNumber}/question`, { question: questionText.trim() });
      toast.success("Rückfrage gesendet");
      if (onUpdate) onUpdate();
      setShowQuestionModal(null);
      setQuestionText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setLoading(false);
    }
  };

  if ((!offers || offers.length === 0) && (!invoices || invoices.length === 0)) return null;

  return (
    <div className="mb-8" data-testid="portal-offers">
      <div className="bg-white border border-[#CBD5E1]">
        <div className="bg-[#0F172A] flex items-center gap-3.5 px-6 py-3.5">
          <span className="text-[0.7rem] font-bold px-2 py-0.5"
            style={{ background: "#D4A373", color: "#0F172A", letterSpacing: "0.1em" }}>
            <FileText className="w-3.5 h-3.5 inline" />
          </span>
          <span className="font-playfair text-base font-semibold text-white">Angebote</span>
          <span className="text-xs text-[#94A3B8] ml-auto">{offers.length} Angebot{offers.length !== 1 ? "e" : ""}</span>
        </div>

        <div className="divide-y divide-[#F1F5F9]">
          {offers.map((offer) => {
            const status = STATUS_MAP[offer.portal_status] || STATUS_MAP.pending;
            const StatusIcon = status.icon;
            const isExpanded = expandedOffer === offer.offer_number;
            const isPending = offer.portal_status === "pending";

            return (
              <div key={offer.offer_number} data-testid={`offer-${offer.offer_number}`}>
                {/* Offer Header Row */}
                <div
                  className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                  onClick={() => setExpandedOffer(isExpanded ? null : offer.offer_number)}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-4 h-4 text-[#D4A373] flex-shrink-0" />
                    <span className="font-semibold text-sm text-[#0F172A] truncate">
                      {offer.offer_number}
                    </span>
                    {offer.client_name && (
                      <span className="text-xs text-[#64748B] truncate hidden sm:inline">
                        — {offer.client_name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {offer.endpreis_chf && (
                      <span className="text-sm font-semibold text-[#0F172A]">
                        {formatCHF(offer.amount_chf || offer.endpreis_chf)}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase px-2 py-1"
                      style={{ background: status.bg, color: status.color, letterSpacing: "0.08em" }}
                      data-testid={`offer-status-${offer.offer_number}`}>
                      <StatusIcon className="w-3 h-3" />
                      {status.label}
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 pt-1 space-y-4 border-t border-[#F1F5F9]">
                        {/* Details Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          {offer.project_name && (
                            <div>
                              <span className="text-[#94A3B8] block mb-0.5">Projekt</span>
                              <span className="font-medium text-[#0F172A]">{offer.project_name}</span>
                            </div>
                          )}
                          {(offer.amount_chf || offer.endpreis_chf) && (
                            <div>
                              <span className="text-[#94A3B8] block mb-0.5">Betrag</span>
                              <span className="font-semibold text-[#0F172A]">{formatCHF(offer.amount_chf || offer.endpreis_chf)}</span>
                            </div>
                          )}
                          {offer.valid_until && (
                            <div>
                              <span className="text-[#94A3B8] block mb-0.5">Gültig bis</span>
                              <span className="font-medium text-[#0F172A]">{formatDate(offer.valid_until)}</span>
                            </div>
                          )}
                          {offer.atlas_received_at && (
                            <div>
                              <span className="text-[#94A3B8] block mb-0.5">Erhalten am</span>
                              <span className="font-medium text-[#0F172A]">{formatDate(offer.atlas_received_at)}</span>
                            </div>
                          )}
                        </div>

                        {offer.atlas_note && (
                          <p className="text-xs text-[#64748B] italic">{offer.atlas_note}</p>
                        )}

                        {/* Client response info */}
                        {offer.client_response_at && (
                          <div className="text-xs text-[#64748B] flex items-center gap-1.5">
                            <MessageSquare className="w-3 h-3" />
                            {offer.portal_status === "accepted" ? "Akzeptiert" : "Abgelehnt"} am {formatDate(offer.client_response_at)}
                            {offer.client_comment && ` — "${offer.client_comment}"`}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-2 flex-wrap">
                          {offer.pdf_url && (
                            <a href={offer.pdf_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-[#0F172A] border border-[#CBD5E1] hover:border-[#D4A373] transition-colors"
                              data-testid={`offer-download-${offer.offer_number}`}>
                              <Download className="w-3.5 h-3.5" /> PDF anzeigen
                            </a>
                          )}

                          <button
                            onClick={(e) => { e.stopPropagation(); setShowQuestionModal(offer.offer_number); }}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-[#D4A373] border border-[#D4A373] hover:bg-[#FFF8F0] transition-colors disabled:opacity-50"
                            data-testid={`offer-question-${offer.offer_number}`}>
                            <HelpCircle className="w-3.5 h-3.5" /> Rückfrage
                          </button>

                          {isPending && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); respond(offer.offer_number, "accepted"); }}
                                disabled={loading}
                                className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold uppercase text-white bg-[#10B981] hover:bg-[#059669] transition-colors disabled:opacity-50"
                                style={{ letterSpacing: "0.08em" }}
                                data-testid={`offer-accept-${offer.offer_number}`}>
                                <Check className="w-3.5 h-3.5" /> Akzeptieren
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowRejectModal(offer.offer_number); }}
                                disabled={loading}
                                className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold uppercase text-[#EF4444] border border-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                                style={{ letterSpacing: "0.08em" }}
                                data-testid={`offer-reject-${offer.offer_number}`}>
                                <X className="w-3.5 h-3.5" /> Ablehnen
                              </button>
                            </>
                          )}
                        </div>

                        {/* Previous questions */}
                        {offer.questions && offer.questions.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            <span className="text-[0.65rem] font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.1em" }}>Rückfragen</span>
                            {offer.questions.map((q, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-[#64748B] bg-[#F8F9FA] p-2 border border-[#F1F5F9]">
                                <HelpCircle className="w-3 h-3 text-[#D4A373] mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="text-[#1E293B]">{q.question}</span>
                                  <span className="text-[#94A3B8] ml-2 text-[0.65rem]">{formatDate(q.asked_at)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Reject Modal */}
                {showRejectModal === offer.offer_number && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowRejectModal(null)}>
                    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      className="bg-white border border-[#CBD5E1] p-6 max-w-md w-full mx-4"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="offer-reject-modal">
                      <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-1">Angebot ablehnen</h3>
                      <p className="text-xs text-[#64748B] mb-4">Bitte teilen Sie uns den Grund mit (optional).</p>
                      <textarea
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                        className="w-full border border-[#CBD5E1] p-3 text-sm min-h-[80px] focus:border-[#D4A373] outline-none resize-vertical"
                        placeholder="Grund für die Ablehnung..."
                        data-testid="offer-reject-comment"
                      />
                      <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => { setShowRejectModal(null); setRejectComment(""); }}
                          className="px-4 py-2 text-xs text-[#64748B] hover:text-[#0F172A]">
                          Abbrechen
                        </button>
                        <button
                          onClick={() => respond(offer.offer_number, "rejected", rejectComment)}
                          disabled={loading}
                          className="px-5 py-2 text-xs font-bold uppercase text-white bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
                          data-testid="offer-reject-confirm">
                          {loading ? "..." : "Ablehnen"}
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Question Modal */}
                {showQuestionModal === offer.offer_number && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowQuestionModal(null)}>
                    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      className="bg-white border border-[#CBD5E1] p-6 max-w-md w-full mx-4"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="offer-question-modal">
                      <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-1">Rückfrage zum Angebot</h3>
                      <p className="text-xs text-[#64748B] mb-1">{offer.offer_number}</p>
                      <p className="text-xs text-[#94A3B8] mb-4">Ihre Frage wird direkt an unser Team weitergeleitet.</p>
                      <textarea
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        className="w-full border border-[#CBD5E1] p-3 text-sm min-h-[100px] focus:border-[#D4A373] outline-none resize-vertical"
                        placeholder="Ihre Frage zum Angebot..."
                        data-testid="offer-question-text"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => { setShowQuestionModal(null); setQuestionText(""); }}
                          className="px-4 py-2 text-xs text-[#64748B] hover:text-[#0F172A]">
                          Abbrechen
                        </button>
                        <button
                          onClick={() => askQuestion(offer.offer_number)}
                          disabled={loading || !questionText.trim()}
                          className="px-5 py-2 text-xs font-bold uppercase text-white bg-[#D4A373] hover:bg-[#B5835A] disabled:opacity-50 transition-colors"
                          data-testid="offer-question-send">
                          {loading ? "..." : "Absenden"}
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoices Section */}
      {invoices && invoices.length > 0 && (
        <div className="bg-white border border-[#CBD5E1] mt-4">
          <div className="bg-[#1E293B] flex items-center gap-3.5 px-6 py-3.5">
            <span className="text-[0.7rem] font-bold px-2 py-0.5"
              style={{ background: "#64748B", color: "#fff", letterSpacing: "0.1em" }}>
              <FileText className="w-3.5 h-3.5 inline" />
            </span>
            <span className="font-playfair text-base font-semibold text-white">Rechnungen</span>
            <span className="text-xs text-[#94A3B8] ml-auto">{invoices.length} Rechnung{invoices.length !== 1 ? "en" : ""}</span>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {invoices.map((inv, i) => (
              <div key={inv.invoice_number || i} className="flex items-center gap-4 px-6 py-4" data-testid={`invoice-${inv.invoice_number || i}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-[#64748B] flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="font-semibold text-sm text-[#0F172A]">{inv.invoice_number || `Rechnung ${i+1}`}</span>
                    {inv.label && <span className="text-xs text-[#64748B] ml-2">{inv.label}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {inv.amount_chf && (
                    <span className="text-sm font-semibold text-[#0F172A]">{formatCHF(inv.amount_chf)}</span>
                  )}
                  {inv.due_date && (
                    <span className="text-xs text-[#64748B]">Fällig: {formatDate(inv.due_date)}</span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase px-2 py-1 ${
                    inv.status === "paid" ? "bg-[#ECFDF5] text-[#10B981]" : "bg-[#FFF8F0] text-[#D4A373]"
                  }`} style={{ letterSpacing: "0.08em" }}>
                    {inv.status === "paid" ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {inv.status === "paid" ? "Bezahlt" : "Offen"}
                  </span>
                  {inv.pdf_url && (
                    <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#0F172A] border border-[#CBD5E1] hover:border-[#D4A373] transition-colors">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
